import React, { useState, useRef, useEffect } from "react";
import {
  Upload, FileSpreadsheet, Wand2, CheckCircle2, AlertTriangle, ArrowRight, ArrowLeft,
  Save, Download, Sparkles, ListChecks, FileCheck2, Link2, Wand,
} from "lucide-react";
import api from "../lib/api";
import { Modal, Button, Select, Badge } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtRp, fmtNum } from "../lib/constants";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const FIELDS = [
  { key: "date", label: "Date", required: true },
  { key: "branch", label: "Branch", required: true, branchOnly: true },
  { key: "menu_item", label: "Menu Item", required: true },
  { key: "quantity", label: "Quantity Sold", required: true },
  { key: "gross_sales", label: "Gross Sales", required: false },
];

const Stepper = ({ step, t }) => {
  const steps = ["Upload", "Map Columns", "Preview", "Done"];
  const idx = { upload: 0, map: 1, preview: 2, summary: 3 }[step] ?? 0;
  return (
    <div className="mb-5 flex items-center gap-1" data-testid="wizard-stepper">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ${i <= idx ? "bg-primary text-white" : "bg-stone-100 text-ink-mute"}`}>
            <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${i < idx ? "bg-white/30" : i === idx ? "bg-white/30" : "bg-stone-200"}`}>{i + 1}</span>
            <span className="hidden sm:inline">{t(s)}</span>
          </div>
          {i < steps.length - 1 && <span className={`h-0.5 w-3 ${i < idx ? "bg-primary" : "bg-stone-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default function SalesImportWizard({ open, onClose, onDone, isBranch }) {
  const { t } = useT();
  const { isRole } = useAuth();
  const canMap = isRole("SUPER_ADMIN", "OWNER");
  const toast = useToast();
  const fileRef = useRef();
  const [mode, setMode] = useState("wizard");      // wizard | template
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detect, setDetect] = useState(null);
  const [mapping, setMapping] = useState({ date: "", branch: "", menu_item: "", quantity: "", gross_sales: "" });
  const [usedTemplate, setUsedTemplate] = useState(null);
  const [saveTpl, setSaveTpl] = useState(false);
  const [tplName, setTplName] = useState("");
  const [preview, setPreview] = useState(null);
  const [legacyPreview, setLegacyPreview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [menus, setMenus] = useState([]);          // central flow menus for the mapping dropdown
  const [manualMaps, setManualMaps] = useState({}); // pos_name -> menu_item_id (session)
  const [saveMaps, setSaveMaps] = useState(true);

  useEffect(() => {
    if (open) api.get("/menu-items?active_only=true").then(({ data }) => setMenus(data)).catch(() => setMenus([]));
  }, [open]);

  const reset = () => {
    setMode("wizard"); setStep("upload"); setFile(null); setDetect(null); setPreview(null);
    setLegacyPreview(null); setSummary(null); setUsedTemplate(null); setSaveTpl(false); setTplName("");
    setManualMaps({}); setSaveMaps(true);
    setMapping({ date: "", branch: "", menu_item: "", quantity: "", gross_sales: "" });
    if (fileRef.current) fileRef.current.value = "";
  };
  const close = () => { reset(); onClose(); };

  // ---- WIZARD: upload -> detect ----
  const onWizardFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/sales/import/detect", fd);
      setDetect(data);
      setMapping({ date: "", branch: "", menu_item: "", quantity: "", gross_sales: "", ...(data.suggested_mapping || {}) });
      setStep("map");
    } catch (err) { toast.error(err.response?.data?.detail || t("Could not read the file")); }
    finally { setBusy(false); }
  };

  const useTemplate = () => {
    if (!detect?.matched_template) return;
    setMapping({ date: "", branch: "", menu_item: "", quantity: "", gross_sales: "", ...(detect.matched_template.mapping || {}) });
    setUsedTemplate(detect.matched_template.name);
  };

  const requiredOk = FIELDS.every((f) => {
    if (f.branchOnly && !detect?.branch_required) return true;
    return !f.required || !!mapping[f.key];
  });

  const goPreview = async (maps) => {
    const useMaps = maps && typeof maps === "object" && !maps.nativeEvent ? maps : manualMaps;
    setBusy(true);
    try {
      if (saveTpl && tplName.trim()) {
        await api.post("/sales/import/templates", { name: tplName.trim(), branch_id: null, mapping, headers: detect.headers })
          .then(() => toast.success(t("Template saved"))).catch(() => {});
      }
      const fd = new FormData(); fd.append("file", file); fd.append("mapping", JSON.stringify(mapping));
      const mm = Object.entries(useMaps).filter(([, v]) => v).map(([pos_name, menu_item_id]) => ({ pos_name, menu_item_id }));
      if (mm.length) fd.append("manual_mappings", JSON.stringify(mm));
      const { data } = await api.post("/sales/import/preview", fd);
      setPreview(data); setStep("preview");
    } catch (err) { toast.error(err.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  const applyMappings = async () => {
    const chosen = Object.entries(manualMaps).filter(([, v]) => v);
    if (!chosen.length) return toast.error(t("Select a menu for at least one item"));
    setBusy(true);
    try {
      if (saveMaps && canMap) {
        await Promise.all(chosen.map(([pos, mid]) =>
          api.post("/sales/menu-mappings", { pos_menu_name: pos, menu_item_id: mid, branch_id: null }).catch(() => {})));
        toast.success(t("Mappings saved"));
      }
      await goPreview(manualMaps);
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    const rows = preview.rows.filter((r) => r.valid).map((r) => ({
      branch_id: r.branch_id, menu_item_id: r.menu_item_id, date: r.date, quantity: r.quantity, gross_sales: r.gross_sales,
    }));
    if (!rows.length) return toast.error(t("No valid rows to import"));
    setBusy(true);
    try {
      const { data } = await api.post("/sales/import/confirm", {
        rows, file_name: file?.name, template_name: usedTemplate || (saveTpl ? tplName.trim() : null),
        skipped: 0, error_rows: preview.invalid,
      });
      setSummary(data); setStep("summary"); onDone();
    } catch (err) { toast.error(err.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  // ---- LEGACY TEMPLATE flow ----
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/sales/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "sales_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("Download failed")); }
  };
  const onLegacyFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/sales/import/parse", fd);
      setLegacyPreview(data);
    } catch (err) { toast.error(err.response?.data?.detail || t("Could not parse file")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const legacyImport = async () => {
    const rows = legacyPreview.rows.filter((r) => r.valid).map((r) => ({
      branch_id: r.branch_id, menu_item_id: r.menu_item_id, date: r.date, quantity: r.quantity, gross_sales: r.gross_sales,
    }));
    if (!rows.length) return toast.error(t("No valid rows to import"));
    setBusy(true);
    try {
      const { data } = await api.post("/sales/import/confirm", {
        rows, file_name: file?.name, template_name: "Central Flow Template", skipped: 0,
        error_rows: legacyPreview.invalid,
      });
      setSummary(data); setStep("summary"); onDone();
    } catch (err) { toast.error(err.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  // ---- footer per step ----
  let footer = null;
  if (step === "summary") {
    footer = <Button onClick={close} data-testid="wizard-done">{t("Done")}</Button>;
  } else if (mode === "wizard" && step === "map") {
    footer = <><Button variant="outline" onClick={() => { setStep("upload"); setDetect(null); }} data-testid="wizard-back-upload"><ArrowLeft size={16} /> {t("Back")}</Button>
      <Button onClick={() => goPreview()} loading={busy} disabled={!requiredOk} data-testid="wizard-next-preview">{t("Next: Preview")} <ArrowRight size={16} /></Button></>;
  } else if (mode === "wizard" && step === "preview") {
    footer = <><Button variant="outline" onClick={() => setStep("map")} data-testid="wizard-back-map"><ArrowLeft size={16} /> {t("Back")}</Button>
      <Button onClick={doImport} loading={busy} disabled={!preview || preview.valid === 0} data-testid="wizard-import">{t("Import")} {preview?.valid} {t("valid")}</Button></>;
  } else if (mode === "template" && legacyPreview) {
    footer = <><Button variant="outline" onClick={() => setLegacyPreview(null)}>{t("Back")}</Button>
      <Button onClick={legacyImport} loading={busy} disabled={legacyPreview.valid === 0} data-testid="legacy-import">{t("Import")} {legacyPreview.valid} {t("valid")}</Button></>;
  } else {
    footer = <Button variant="outline" onClick={close}>{t("Close")}</Button>;
  }

  return (
    <Modal open={open} onClose={close} size="lg" title={t("Sales Import Wizard")} footer={footer}>
      {step !== "summary" && <Stepper step={mode === "template" ? (legacyPreview ? "preview" : "upload") : step} t={t} />}

      {/* method toggle (only on first screen) */}
      {step === "upload" && (
        <div className="mb-4 flex gap-2" data-testid="import-method-toggle">
          <button onClick={() => { setMode("wizard"); setLegacyPreview(null); }} data-testid="method-wizard"
            className={`flex flex-1 items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-left text-sm font-semibold transition-all ${mode === "wizard" ? "border-primary bg-primary-light/40 text-ink" : "border-line text-ink-soft hover:bg-stone-50"}`}>
            <Wand2 size={18} className="text-primary" /> {t("Wizard (any POS export)")}
          </button>
          <button onClick={() => setMode("template")} data-testid="method-template"
            className={`flex flex-1 items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-left text-sm font-semibold transition-all ${mode === "template" ? "border-primary bg-primary-light/40 text-ink" : "border-line text-ink-soft hover:bg-stone-50"}`}>
            <FileSpreadsheet size={18} className="text-primary" /> {t("Central Flow Template")}
          </button>
        </div>
      )}

      {/* ===== WIZARD UPLOAD ===== */}
      {mode === "wizard" && step === "upload" && (
        <div className="space-y-3">
          <p className="text-sm text-ink-soft">{t("Upload any POS export (CSV, XLS, XLSX). We'll detect the columns automatically.")} {isBranch && t("Your branch is applied automatically.")}</p>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-line py-10 text-ink-soft hover:bg-stone-50" data-testid="wizard-dropzone">
            <Upload size={28} className="text-primary" />
            <span className="text-sm font-semibold">{busy ? t("Reading file…") : t("Choose CSV / XLS / XLSX file")}</span>
            <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx,.xlsm" onChange={onWizardFile} className="hidden" data-testid="wizard-file-input" />
          </label>
        </div>
      )}

      {/* ===== WIZARD MAP ===== */}
      {mode === "wizard" && step === "map" && detect && (
        <div className="space-y-4">
          {detect.matched_template && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary-light/40 p-3" data-testid="template-match-banner">
              <Sparkles size={16} className="text-primary" />
              <span className="text-sm font-semibold text-ink">{t("Template found:")} <b>{detect.matched_template.name}</b> ({detect.matched_template.confidence}%)</span>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" onClick={useTemplate} data-testid="use-template-btn"><FileCheck2 size={14} /> {t("Use Template")}</Button>
              </div>
            </div>
          )}
          {detect.duplicate_headers?.length > 0 && (
            <div className="flex items-center gap-2 rounded-xl bg-amber-50 p-2.5 text-xs font-semibold text-amber-800" data-testid="duplicate-warning">
              <AlertTriangle size={14} /> {t("Duplicate column names detected:")} {detect.duplicate_headers.join(", ")}
            </div>
          )}
          {/* Diagnostics panel — detected headers (helps debug odd POS exports) */}
          <div className="rounded-xl bg-stone-50 px-3 py-2 text-xs" data-testid="detect-diagnostics">
            <span className="font-semibold text-ink-soft">{t("Detected")}: </span>
            <span className="text-ink-mute">{detect.total} {t("rows")} · {(detect.headers || []).length} {t("columns")}</span>
            {(detect.headers || []).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1" data-testid="detect-headers">
                {detect.headers.map((h, i) => (
                  <span key={i} className="rounded border border-line bg-white px-1.5 py-0.5 text-[11px] text-ink-soft">{h}</span>
                ))}
              </div>
            )}
          </div>

          {(detect.headers || []).length === 0 ? (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-danger/30 bg-danger-light/40 p-3 text-sm font-semibold text-danger-text" data-testid="no-headers-error">
              <AlertTriangle size={16} /> {t("No column headers detected")}
              <Button variant="outline" className="ml-auto" onClick={() => { setStep("upload"); setDetect(null); }} data-testid="reupload-btn">{t("Re-upload")}</Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-ink-soft">{t("Map each Central Flow field to a column from your file.")} <span className="text-ink-mute">({detect.total} {t("rows detected")})</span></p>
              <div className="space-y-2.5">
                {FIELDS.filter((f) => !(f.branchOnly && !detect.branch_required)).map((f) => {
                  const col = mapping[f.key];
                  const sample = col ? (detect.sample_rows || []).map((r) => r[col]).find((v) => v != null && String(v).trim() !== "") : null;
                  return (
                    <div key={f.key} className="grid grid-cols-2 items-start gap-3" data-testid={`map-row-${f.key}`}>
                      <div className="flex items-center gap-2 pt-2 text-sm font-semibold text-ink">
                        {t(f.label)}
                        {f.required ? <Badge tone="danger">{t("Mandatory")}</Badge> : <Badge tone="mute">{t("Optional")}</Badge>}
                      </div>
                      <div>
                        <Select value={mapping[f.key] || ""} onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })} data-testid={`map-select-${f.key}`}>
                          <option value="">{f.required ? t("Choose a column…") : t("— none —")}</option>
                          {detect.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                        </Select>
                        {sample != null && (
                          <p className="mt-1 truncate text-[11px] text-ink-mute" data-testid={`map-sample-${f.key}`} title={String(sample)}>
                            {t("Sample")}: <span className="font-medium text-ink-soft">{String(sample)}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="rounded-xl border border-line p-3">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input type="checkbox" checked={saveTpl} onChange={(e) => setSaveTpl(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="save-template-toggle" />
                  <Save size={15} className="text-primary" /> {t("Save this mapping as a template")}
                </label>
                {saveTpl && (
                  <input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder={t("e.g. Moka Export, Majoo Export")}
                    className="cf-input mt-2" data-testid="save-template-name" />
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== WIZARD PREVIEW ===== */}
      {mode === "wizard" && step === "preview" && preview && (
        <>
          {preview.auto_mapped > 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700" data-testid="auto-mapped-note">
              <Wand size={14} /> {preview.auto_mapped} {t("row(s) auto-mapped from saved menu mappings")}
            </div>
          )}
          {preview.needs_mapping > 0 && (preview.unknown_menus || []).length > 0 && (
            <div className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-3" data-testid="unknown-menu-panel">
              <div className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-900">
                <Link2 size={16} /> {t("Unknown Menu Detected")} ({preview.unknown_menus.length})
              </div>
              <p className="mb-2 text-xs text-amber-800">{t("Match each POS menu name to a Central Flow menu.")} {!canMap && t("(Ask an admin to save mappings.)")}</p>
              <div className="space-y-2">
                {preview.unknown_menus.map((u, i) => (
                  <div key={i} className="grid grid-cols-2 items-center gap-2" data-testid={`unknown-row-${i}`}>
                    <div className="min-w-0 text-sm">
                      <span className="block truncate font-semibold text-ink" title={u.pos_name}>{u.pos_name}</span>
                      <span className="text-[11px] text-ink-mute">{u.count} {t("row(s)")}</span>
                    </div>
                    <Select value={manualMaps[u.pos_name] || ""} onChange={(e) => setManualMaps({ ...manualMaps, [u.pos_name]: e.target.value })} data-testid={`unknown-select-${i}`}>
                      <option value="">{t("Select Central Flow Menu")}</option>
                      {menus.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </Select>
                  </div>
                ))}
              </div>
              {canMap && (
                <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-amber-900">
                  <input type="checkbox" checked={saveMaps} onChange={(e) => setSaveMaps(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="remember-mapping-toggle" />
                  {t("Remember mapping — save for future imports")}
                </label>
              )}
              <Button className="mt-2" onClick={applyMappings} loading={busy} data-testid="apply-mapping-btn"><Wand2 size={15} /> {t("Apply Mapping")}</Button>
            </div>
          )}
          <PreviewBlock preview={preview} showErrors={showErrors} setShowErrors={setShowErrors} t={t} />
        </>
      )}

      {/* ===== SUMMARY ===== */}
      {step === "summary" && summary && (
        <div className="space-y-4 text-center" data-testid="import-summary">
          <div className="flex flex-col items-center gap-2 py-2">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success-light text-success-text"><CheckCircle2 size={30} /></span>
            <h3 className="font-heading text-lg font-extrabold text-ink">{t("Import Complete")}</h3>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label={t("Imported")} value={fmtNum(summary.imported)} tone="success" testid="summary-imported" />
            <Stat label={t("Skipped")} value={fmtNum(summary.skipped)} tone="mute" testid="summary-skipped" />
            <Stat label={t("Errors")} value={fmtNum(summary.errors)} tone={summary.errors ? "danger" : "mute"} testid="summary-errors" />
            <Stat label={t("Total Qty")} value={fmtNum(summary.total_qty)} tone="info" testid="summary-qty" />
            <Stat label={t("Gross Sales")} value={fmtRp(summary.total_gross)} tone="primary" testid="summary-gross" />
            {summary.consumption && <Stat label={t("Ingredients Consumed")} value={fmtNum(summary.consumption.products_consumed)} tone="info" testid="summary-consumed" />}
          </div>
          {summary.consumption?.products_consumed > 0 && (
            <p className="rounded-xl bg-blue-50 px-3 py-2 text-left text-xs text-blue-700" data-testid="summary-consumption-note">
              {t("Branch ingredient stock was automatically reduced based on each menu's recipe (BOM).")}
            </p>
          )}
          {summary.consumption?.missing_recipe_menus?.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-left text-xs text-amber-800" data-testid="summary-missing-recipe">
              <div className="mb-1 flex items-center gap-1.5 font-bold"><AlertTriangle size={13} /> {t("Missing Recipe — not consumed:")}</div>
              <div>{summary.consumption.missing_recipe_menus.join(", ")}</div>
            </div>
          )}
        </div>
      )}

      {/* ===== LEGACY TEMPLATE ===== */}
      {mode === "template" && step === "upload" && !legacyPreview && (
        <div className="space-y-4">
          <p className="text-sm text-ink-soft">{t("Upload a CSV or Excel file with columns:")} <b>Date | Branch | Menu Item | Quantity Sold | Gross Sales</b> ({t("Gross optional")}). {isBranch && t("Your branch is applied automatically.")}</p>
          <button onClick={downloadTemplate} className="inline-flex items-center gap-2 text-sm font-semibold text-primary" data-testid="download-template"><Download size={15} /> {t("Download template")}</button>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-line py-10 text-ink-soft hover:bg-stone-50" data-testid="import-dropzone">
            <FileSpreadsheet size={28} className="text-primary" />
            <span className="text-sm font-semibold">{busy ? t("Parsing…") : t("Choose CSV / XLSX file")}</span>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" onChange={onLegacyFile} className="hidden" data-testid="import-file-input" />
          </label>
        </div>
      )}
      {mode === "template" && legacyPreview && (
        <PreviewBlock preview={{ ...legacyPreview, preview_rows: legacyPreview.rows.slice(0, 20), totals: null }}
          showErrors={showErrors} setShowErrors={setShowErrors} t={t} legacy />
      )}
    </Modal>
  );
}

function PreviewBlock({ preview, showErrors, setShowErrors, t, legacy }) {
  return (
    <div>
      {preview.totals && (
        <div className="mb-3 grid grid-cols-3 gap-3" data-testid="preview-stats">
          <Stat label={t("Row Count")} value={fmtNum(preview.total)} tone="info" testid="preview-rowcount" />
          <Stat label={t("Total Qty")} value={fmtNum(preview.totals.quantity)} tone="primary" testid="preview-qty" />
          <Stat label={t("Estimated Sales")} value={fmtRp(preview.totals.gross_sales)} tone="success" testid="preview-sales" />
        </div>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Badge tone="success"><CheckCircle2 size={12} /> {preview.valid} {t("valid")}</Badge>
        {preview.invalid > 0 && (
          <>
            <Badge tone="danger"><AlertTriangle size={12} /> {preview.invalid} {t("invalid")}</Badge>
            {!legacy && <button onClick={() => setShowErrors((s) => !s)} className="text-xs font-semibold text-primary" data-testid="toggle-errors"><ListChecks size={12} className="inline" /> {showErrors ? t("Hide errors") : t("View errors")}</button>}
          </>
        )}
      </div>
      {showErrors && preview.errors?.length > 0 && (
        <div className="mb-3 max-h-32 overflow-auto rounded-xl border border-danger/30 bg-danger-light/30 p-2 text-xs" data-testid="error-list">
          {preview.errors.map((e, i) => (
            <div key={i} className="py-0.5"><b>{t("Row")} {e.row}:</b> <span className="text-danger-text">{e.error}</span></div>
          ))}
        </div>
      )}
      <p className="mb-1 text-[11px] text-ink-mute">{t("Showing first 20 rows")}</p>
      <div className="max-h-72 overflow-auto rounded-xl border border-line">
        <table className="w-full text-xs" data-testid="import-preview">
          <thead className="sticky top-0 bg-stone-50"><tr className="text-left text-ink-mute">
            <th className="px-3 py-2">{t("Parsed Date")}</th><th className="px-3 py-2">{t("Original Value")}</th><th className="px-3 py-2">{t("Branch")}</th><th className="px-3 py-2">{t("Menu")}</th>
            <th className="px-3 py-2 text-right">{t("Qty")}</th><th className="px-3 py-2 text-right">{t("Gross")}</th><th className="px-3 py-2">{t("Status")}</th></tr></thead>
          <tbody>
            {(preview.preview_rows || []).map((r, i) => (
              <tr key={i} className={`border-t border-line/60 ${r.valid ? "" : "bg-danger-light/30"}`}>
                <td className="px-3 py-1.5">{r.date ? <span className="font-medium text-ink">{r.date}</span> : <span className="text-danger-text">—</span>}</td>
                <td className="px-3 py-1.5 text-ink-mute" title={r.date_raw}>{r.date_raw ? <span className="block max-w-[180px] truncate">{r.date_raw}</span> : "—"}</td>
                <td className="px-3 py-1.5">{r.branch_name || "—"}</td>
                <td className="px-3 py-1.5">{r.menu_item_name || "—"}</td>
                <td className="px-3 py-1.5 text-right">{r.quantity ?? "—"}</td>
                <td className="px-3 py-1.5 text-right">{r.gross_sales ? fmtRp(r.gross_sales) : "—"}</td>
                <td className="px-3 py-1.5">{r.valid ? <Badge tone="success">OK</Badge> : <span className="text-danger-text">{r.error}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "primary", testid }) {
  const tones = { primary: "text-primary", success: "text-success-text", info: "text-blue-600", danger: "text-danger-text", mute: "text-ink-soft" };
  return (
    <div className="rounded-xl bg-stone-50 p-3 text-center" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase text-ink-mute">{label}</p>
      <p className={`text-base font-extrabold ${tones[tone]}`}>{value}</p>
    </div>
  );
}
